import React, { useCallback, useEffect, useState } from 'react';

// Performance tab — the engagement funnel + per-member breakdown, synced back
// from the outreach tool via webhooks (Phase D). Reads
// GET /campaigns/:id/performance.

interface Funnel {
  launched: number; opened: number; clicked: number; replied: number;
  interested: number; bounced: number; unsubscribed: number;
}
interface PerfMember {
  entity_type: 'candidate' | 'contact';
  entity_id: number;
  name: string;
  stage: string;
  opened_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  interested: boolean;
}

const STAGE_STYLE: Record<string, string> = {
  interested: 'bg-emerald-100 text-emerald-700',
  replied: 'bg-blue-100 text-blue-700',
  clicked: 'bg-indigo-100 text-indigo-700',
  opened: 'bg-sky-100 text-sky-700',
  sent: 'bg-gray-100 text-gray-500',
  bounced: 'bg-red-100 text-red-700',
  unsubscribed: 'bg-amber-100 text-amber-700',
};

const CampaignPerformance: React.FC<{ campaignId: string; token: string | null }> = ({ campaignId, token }) => {
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [members, setMembers] = useState<PerfMember[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const res = await fetch(`/api/campaigns/${campaignId}/performance`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setFunnel(data.funnel);
      setMembers(data.members || []);
    }
    setLoading(false);
  }, [campaignId, token]);

  useEffect(() => { load(); }, [load]);

  const pct = (n: number) => (funnel && funnel.launched > 0 ? Math.round((n / funnel.launched) * 100) : 0);

  const stat = (label: string, value: number, rate?: number, tone = 'text-gray-800') => (
    <div className="card p-4">
      <div className={`text-2xl font-semibold ${tone}`}>{value}{rate !== undefined && <span className="text-sm font-normal text-gray-400 ml-1">{rate}%</span>}</div>
      <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide">{label}</div>
    </div>
  );

  if (loading) return <div className="card p-10 text-center text-gray-400 text-sm">Loading performance…</div>;

  const launched = funnel?.launched ?? 0;

  return (
    <div className="space-y-5">
      {launched === 0 ? (
        <div className="card p-10 text-center text-gray-500">
          <h3 className="font-semibold text-gray-800 mb-1">No members launched yet</h3>
          <p className="text-sm max-w-md mx-auto">Push members to lemlist / SourceWhale from the <strong>Launch</strong> tab. Once they’re sent, opens, replies, and interested-flags will appear here as your outreach tool reports them.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {stat('Launched', funnel!.launched)}
            {stat('Opened', funnel!.opened, pct(funnel!.opened), 'text-sky-600')}
            {stat('Replied', funnel!.replied, pct(funnel!.replied), 'text-blue-600')}
            {stat('Interested', funnel!.interested, pct(funnel!.interested), 'text-emerald-600')}
            {stat('Bounced', funnel!.bounced, pct(funnel!.bounced), 'text-red-600')}
            {stat('Unsub', funnel!.unsubscribed, pct(funnel!.unsubscribed), 'text-amber-600')}
          </div>

          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Member</th>
                  <th className="text-left font-medium px-4 py-2">Type</th>
                  <th className="text-left font-medium px-4 py-2">Stage</th>
                  <th className="text-left font-medium px-4 py-2">Opened</th>
                  <th className="text-left font-medium px-4 py-2">Replied</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map(m => (
                  <tr key={`${m.entity_type}-${m.entity_id}`}>
                    <td className="px-4 py-2 text-gray-800">{m.name}</td>
                    <td className="px-4 py-2 text-gray-500 capitalize">{m.entity_type}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STAGE_STYLE[m.stage] || 'bg-gray-100 text-gray-500'}`}>{m.stage}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-500">{m.opened_at ? '✓' : '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{m.replied_at ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CampaignPerformance;
